/**
 * Owner: Masa Bayu
 * File: host-cdn-delivery.ts
 * Date:Time: 2026-09-21 07:22
 * Description: P6-08 CDN asset delivery — read-only · no QXP/QXW mutation.
 */

import {
  findHostCdnAsset,
  getActiveHostCatalogStore,
  type HostCdnAssetKind,
  type HostCdnAssetRecord,
  type HostCatalogStore,
} from './host-catalog-store';

export type HostCdnResolution =
  | {
      readonly ok: true;
      readonly asset: HostCdnAssetRecord;
      readonly url: string;
      readonly kind: HostCdnAssetKind;
      readonly mutatesQxp: false;
      readonly mutatesQxw: false;
      readonly isContentPlane: false;
      readonly exposesQxw: false;
    }
  | {
      readonly ok: false;
      readonly reason: string;
      readonly mutatesQxp: false;
      readonly mutatesQxw: false;
      readonly isContentPlane: false;
      readonly exposesQxw: false;
    };

export function resolveHostCdnAsset(
  assetRef: string,
  store: HostCatalogStore | null = getActiveHostCatalogStore(),
): HostCdnResolution {
  const ref = assetRef.trim();
  if (ref.length === 0) {
    return Object.freeze({
      ok: false as const,
      reason: 'empty asset ref',
      mutatesQxp: false as const,
      mutatesQxw: false as const,
      isContentPlane: false as const,
      exposesQxw: false as const,
    });
  }
  // Fail closed: never serve QXW/source as CDN payload
  if (ref.startsWith('qxw') || ref.includes('/qxw/') || ref.includes('source_doc')) {
    return Object.freeze({
      ok: false as const,
      reason: 'QXW/source assets are not CDN catalog payloads',
      mutatesQxp: false as const,
      mutatesQxw: false as const,
      isContentPlane: false as const,
      exposesQxw: false as const,
    });
  }
  if (store === null) {
    return Object.freeze({
      ok: false as const,
      reason: 'host catalog store not bound',
      mutatesQxp: false as const,
      mutatesQxw: false as const,
      isContentPlane: false as const,
      exposesQxw: false as const,
    });
  }
  const asset = findHostCdnAsset(store, ref);
  if (asset === null) {
    return Object.freeze({
      ok: false as const,
      reason: 'asset not in host CDN map',
      mutatesQxp: false as const,
      mutatesQxw: false as const,
      isContentPlane: false as const,
      exposesQxw: false as const,
    });
  }
  return Object.freeze({
    ok: true as const,
    asset,
    url: asset.url,
    kind: asset.kind,
    mutatesQxp: false as const,
    mutatesQxw: false as const,
    isContentPlane: false as const,
    exposesQxw: false as const,
  });
}

/** Cover URL from host CDN when bound; null if unavailable (caller may fall back). */
export function resolveHostCoverUrl(
  coverAssetRef: string | null | undefined,
): string | null {
  if (coverAssetRef === null || coverAssetRef === undefined) return null;
  const resolved = resolveHostCdnAsset(coverAssetRef);
  return resolved.ok ? resolved.url : null;
}

export function resolveHostQxpPackageUrl(
  qxpPublicationRef: string,
  store: HostCatalogStore | null = getActiveHostCatalogStore(),
): HostCdnResolution {
  return resolveHostCdnAsset(qxpPublicationRef, store);
}
